import React, { FC, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDebouncedCallback } from "@evergis/react";

import { TableHeightDrag as StyledTableHeightDrag } from "../styled";

import { FEATURE_CARD_MIN_HEIGHT } from "../../FeatureCard/constants";
import { MIN_TABLE_HEIGHT } from "../constants";

import { resizeBottomContainer } from "../../../ducks/layout/layout";
import { getBottomHeight, getBottomState } from "../../../ducks/layout/selectors";

interface TableHeightDragProps {
  setIsResizing?: (value: boolean) => void;
}

export const TableHeightDrag: FC<TableHeightDragProps> = ({ setIsResizing }) => {
  const dispatch = useDispatch();
  const bottomState = useSelector(getBottomState);
  const isTableFullscreen = useMemo(() => bottomState === "fullscreen", [bottomState]);
  const bottomHeight = useSelector(getBottomHeight);
  const debounce = useDebouncedCallback(10);

  const onPointerDown = useCallback(
    pointerDownEvent => {
      setIsResizing(true);

      const startPosition = { y: pointerDownEvent.pageY };

      function onPointerMove(pointerMoveEvent) {
        const newHeight = bottomHeight + startPosition.y - pointerMoveEvent.pageY;
        const maxHeight = document.body.offsetHeight - FEATURE_CARD_MIN_HEIGHT;

        document.body.style.userSelect = "none";

        if (newHeight >= MIN_TABLE_HEIGHT && newHeight < maxHeight) {
          debounce(() => {
            dispatch(resizeBottomContainer(newHeight));
          });
        }
      }

      function onPointerUp() {
        setIsResizing(false);
        document.body.style.userSelect = "initial";
        document.body.removeEventListener("pointermove", onPointerMove);
      }

      document.body.addEventListener("pointermove", onPointerMove);
      document.body.addEventListener("pointerup", onPointerUp, { once: true });
    },
    [bottomHeight, debounce, dispatch, setIsResizing],
  );

  return <StyledTableHeightDrag onPointerDown={onPointerDown} disabled={isTableFullscreen} />;
};
